export type Tone = 'success' | 'warning' | 'danger' | 'neutral' | 'info'

const ACRONYMS: Record<string, string> = {
  bca: 'BCA',
  ekyc: 'eKYC',
  mt4: 'MT4',
  mt5: 'MT5',
  ib: 'IB',
  wp: 'WP',
}

export function humanize(raw: string): string {
  const parts = raw.trim().split(/[_.\-]+/).filter(Boolean)
  if (!parts.length) return '—'
  return parts
    .map((token, i) => {
      const key = token.toLowerCase()
      if (ACRONYMS[key]) return ACRONYMS[key]
      return i === 0 ? key[0].toUpperCase() + key.slice(1) : key
    })
    .join(' ')
}

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pending',
  PROCESSING: 'Processing',
  SERVER_PROCESSING: 'Processing (server)',
  MATCHED: 'Matched',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  CONFIRMED: 'Active',
  FORCE_CHANGE_PASSWORD: 'Must change password',
  RESET_REQUIRED: 'Must change password',
}

export function statusTone(status: string): Tone {
  const s = status.toUpperCase()
  if (/REJECT|FAIL|DENIED|CANCEL|INACTIVE/.test(s)) return 'danger'
  // Matched still waits for a person to approve, so it reads as "needs a decision", not done.
  if (/MATCHED|PENDING|SUBMITTED|REVIEW|WAIT|PASSWORD|REQUIRED/.test(s)) return 'warning'
  if (/PROCESS/.test(s)) return 'info'
  if (/APPROVED|ACTIVE|SUCCESS|PASSED|^PASS$/.test(s)) return 'success'
  return 'neutral'
}

export function statusLabel(status: string): string {
  return STATUS_LABELS[status.toUpperCase()] ?? humanize(status)
}

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  superadmin: 'Super admin',
  director: 'Director',
  dealing_settlement: 'Dealing & settlement',
}

export function roleLabel(role?: string | null): string {
  if (!role) return '—'
  const key = role.trim().toLowerCase().replace(/\s+/g, '_')
  return ROLE_LABELS[key] ?? humanize(role)
}

/** Avatar placeholder from the email's local part: figo@ -> FI, john.doe@ -> JD. Cognito gives no name or photo. */
export function initials(email?: string | null): string {
  const parts = (email || '').split('@')[0].split(/[._-]+/).filter(Boolean)
  const two = parts.length > 1 ? parts[0][0] + parts[1][0] : (parts[0] || '').slice(0, 2)
  return two.toUpperCase() || '?'
}

const ACTION_LABELS: Record<string, string> = {
  'funds.approve': 'Approved fund request',
  'funds.reject': 'Rejected fund request',
  'funds.withdraw.dealing_approve': 'Approved withdrawal (dealing)',
  'funds.withdraw.finance_approve': 'Approved withdrawal (finance)',
  'bank.write_blocked': 'Blocked company bank change',
  'client_bank.reveal': 'Revealed client account number',
  'bank.upsert': 'Saved company bank',
  'rate.add': 'Added exchange rate',
  'rate.patch': 'Changed exchange rate status',
  'bca.import': 'Imported BCA CSV',
  'opening.approve': 'Approved agreement',
  'opening.reject': 'Rejected agreement',
  'opening.request-info': 'Requested more information',
  'bank.patch': 'Changed company bank status',
  'bank.delete': 'Deleted company bank',
  'rate.delete': 'Deleted exchange rate',
  'profile.patch': 'Updated client profile',
}

export function actionLabel(action?: string | null): string {
  if (!action) return '—'
  return ACTION_LABELS[action] ?? humanize(action)
}

const METHOD_LABELS: Record<string, string> = {
  virtual_account: 'Virtual account',
  bank_transfer: 'Bank transfer',
}

export function methodLabel(method?: string | null): string {
  if (!method) return '—'
  const key = method.trim().toLowerCase()
  return METHOD_LABELS[key] ?? humanize(method)
}

/** Book and platform codes. mt4/mt5 stay acronyms; a suffix is title-cased. */
export function bookLabel(code?: string | null): string {
  if (!code?.trim()) return '—'
  const parts = code.trim().split(/[_-]+/).filter(Boolean)
  if (!parts.length) return '—'
  return parts
    .map((part) => {
      const key = part.toLowerCase()
      if (ACRONYMS[key]) return ACRONYMS[key]
      return key[0].toUpperCase() + key.slice(1)
    })
    .join(' ')
}

export const platformLabel = bookLabel

export function accountTypeLabel(type?: string | null): string {
  const key = (type || '').trim().toLowerCase()
  if (key === 'real') return 'Live'
  if (key === 'demo') return 'Demo'
  if (!key) return '—'
  return humanize(type || '')
}

export function optionLabels(values: string[], label: (value: string) => string): { value: string; label: string }[] {
  return values.map((value) => ({ value, label: label(value) }))
}
